"use client";

import { buttonVariants } from "@/components/ui/button";
import type { TenantCode } from "@/config/tenants";
import { authCopy } from "@/features/auth/content/auth-copy";
import { buildGoogleOAuthUrl } from "@/features/auth/google-oauth";
import { useAppLocale } from "@/features/localization/app-locale-provider";

interface SocialAuthButtonsProps {
  tenantCode: TenantCode;
}

export function SocialAuthButtons({ tenantCode }: Readonly<SocialAuthButtonsProps>) {
  const { locale } = useAppLocale();
  const googleOAuthUrl = buildGoogleOAuthUrl(tenantCode);
  const label = authCopy[locale].signIn.socialProviders.google;

  return (
    <div className="space-y-3 xl:[@media(max-height:800px)]:space-y-2.5" aria-label="Social sign-in options">
      <a
        aria-label={label}
        className={buttonVariants({
          variant: "outline",
          className: "relative h-[52px] w-full justify-center rounded-xl border-border bg-card px-12 text-[15px] font-medium text-card-foreground shadow-xs transition-[transform,border-color,box-shadow,background-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-ring/50 hover:bg-muted/30 hover:shadow-md aria-disabled:pointer-events-none aria-disabled:opacity-70 2xl:h-14 2xl:text-base",
        })}
        href={googleOAuthUrl}
      >
        <span className="absolute left-4 flex items-center">
          <svg aria-hidden="true" className="size-5 2xl:size-[22px]" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.24-.2-1.79H12v3.4h5.52a4.72 4.72 0 0 1-2.05 3.1l-.02.11 2.98 2.31.21.02c1.94-1.8 3.06-4.43 3.06-7.15Z" />
            <path fill="#34A853" d="M12 22c2.7 0 4.96-.89 6.61-2.42l-3.15-2.44c-.84.57-1.97.97-3.46.97a6 6 0 0 1-5.68-4.14l-.11.01-3.1 2.4-.04.1A10 10 0 0 0 12 22Z" />
            <path fill="#FBBC05" d="M6.32 13.97A6.17 6.17 0 0 1 6 12c0-.68.12-1.34.31-1.97v-.12L3.18 7.47l-.1.05A10 10 0 0 0 2 12c0 1.62.39 3.15 1.07 4.48l3.25-2.51Z" />
            <path fill="#EA4335" d="M12 5.89c1.88 0 3.14.81 3.86 1.48l2.82-2.75C16.95 3.01 14.7 2 12 2a10 10 0 0 0-8.93 5.52l3.24 2.51A6.02 6.02 0 0 1 12 5.89Z" />
          </svg>
        </span>
        <span>{label}</span>
      </a>
    </div>
  );
}
